/**
 * ShippingAddressDrawer — final checkout step for delivery orders.
 *
 * Shows the customer's saved addresses (if any) to pick from, or an inline
 * form to enter a new one. On confirm the order is submitted immediately.
 *
 * Props:
 *   open            {boolean}
 *   onClose         {function}
 *   primaryColor    {string}
 *   savedAddresses  {Array}   — customer.addresses[] (may be empty for guests)
 *   submitting      {boolean}
 *   submitError     {string}
 *   onConfirm       {function(address)} — called with the chosen/entered address
 */

import { useState, useEffect } from "react";
import { AlertCircle, Check, LoaderCircle, MapPin, Pencil, Plus, Star, X } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "./ui/drawer.jsx";

const EMPTY_FORM = { label: "", street: "", city: "", province: "", zip: "", country: "IT" };

function AddressCard({ addr, selected, primaryColor, onSelect, onEdit }) {
  const line1 = addr.street || "";
  const line2 = [addr.zip, addr.city, addr.province, addr.country].filter(Boolean).join(" ");

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
      className="w-full cursor-pointer rounded-2xl border-2 p-4 text-left transition-all"
      style={{
        borderColor: selected ? primaryColor : "rgba(203,213,225,0.7)",
        backgroundColor: selected ? `${primaryColor}10` : "white",
      }}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {addr.isDefault ? <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 shrink-0" /> : <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
            {addr.label ? (
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{addr.label}</span>
            ) : null}
          </div>
          {line1 ? <p className="mt-0.5 text-sm font-semibold text-slate-800">{line1}</p> : null}
          {line2 ? <p className="text-xs text-slate-500">{line2}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              aria-label="Modifica indirizzo"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          <div
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2"
            style={{
              borderColor: selected ? primaryColor : "#cbd5e1",
              backgroundColor: selected ? primaryColor : "transparent",
            }}
          >
            {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewAddressForm({ value, onChange, primaryColor }) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Etichetta (opzionale)
        </label>
        <input
          type="text"
          value={value.label}
          onChange={(e) => onChange("label", e.target.value)}
          placeholder="es. Casa, Ufficio…"
          className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
          style={{ "--primary": primaryColor }}
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Via / Piazza *
        </label>
        <input
          type="text"
          value={value.street}
          onChange={(e) => onChange("street", e.target.value)}
          placeholder="Via Roma 1"
          className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
          style={{ "--primary": primaryColor }}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Città *
          </label>
          <input
            type="text"
            value={value.city}
            onChange={(e) => onChange("city", e.target.value)}
            placeholder="Milano"
            className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
            style={{ "--primary": primaryColor }}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Provincia
          </label>
          <input
            type="text"
            value={value.province}
            onChange={(e) => onChange("province", e.target.value)}
            placeholder="MI"
            maxLength={4}
            className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
            style={{ "--primary": primaryColor }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            CAP *
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={value.zip}
            onChange={(e) => onChange("zip", e.target.value)}
            placeholder="20100"
            className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
            style={{ "--primary": primaryColor }}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Paese
          </label>
          <input
            type="text"
            value={value.country}
            onChange={(e) => onChange("country", e.target.value)}
            placeholder="IT"
            className="h-11 w-full rounded-2xl border border-[#d1dde8] bg-[#f8fbff] px-4 text-sm outline-none transition focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
            style={{ "--primary": primaryColor }}
          />
        </div>
      </div>
    </div>
  );
}

export default function ShippingAddressDrawer({
  open,
  onClose,
  primaryColor = "#38bdf8",
  savedAddresses = [],
  submitting = false,
  submitError = "",
  onConfirm,
}) {
  // "pick" mode: select from list; "new" mode: enter manually
  const hasSaved = savedAddresses.length > 0;
  const [mode, setMode] = useState(hasSaved ? "pick" : "new");
  const defaultIdx = savedAddresses.findIndex((a) => a.isDefault);
  const [selectedIdx, setSelectedIdx] = useState(defaultIdx >= 0 ? defaultIdx : 0);
  const [newForm, setNewForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [editIdx, setEditIdx] = useState(null);   // which saved address is being edited
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  // Reset state every time the drawer opens so it reflects current addresses
  useEffect(() => {
    if (!open) return;
    const has = savedAddresses.length > 0;
    setMode(has ? "pick" : "new");
    const defIdx = savedAddresses.findIndex((a) => a.isDefault);
    setSelectedIdx(defIdx >= 0 ? defIdx : 0);
    setNewForm(EMPTY_FORM);
    setEditIdx(null);
    setEditForm(EMPTY_FORM);
    setFormError("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleFormChange(field, value) {
    setNewForm((prev) => ({ ...prev, [field]: value }));
    setFormError("");
  }

  function handleConfirm() {
    // Edit mode: validate and confirm the edited address
    if (editIdx !== null) {
      if (!editForm.street.trim()) { setFormError("La via è obbligatoria."); return; }
      if (!editForm.city.trim()) { setFormError("La città è obbligatoria."); return; }
      if (!editForm.zip.trim()) { setFormError("Il CAP è obbligatorio."); return; }
      onConfirm({ ...editForm });
      return;
    }

    if (mode === "pick") {
      const addr = savedAddresses[selectedIdx];
      if (!addr) { setFormError("Seleziona un indirizzo."); return; }
      onConfirm({ label: addr.label || "", street: addr.street || "", city: addr.city || "", province: addr.province || "", zip: addr.zip || "", country: addr.country || "" });
      return;
    }

    // new form validation
    if (!newForm.street.trim()) { setFormError("La via è obbligatoria."); return; }
    if (!newForm.city.trim()) { setFormError("La città è obbligatoria."); return; }
    if (!newForm.zip.trim()) { setFormError("Il CAP è obbligatorio."); return; }
    onConfirm({ ...newForm });
  }

  function handleSwitchMode() {
    setFormError("");
    setMode((m) => (m === "pick" ? "new" : "pick"));
  }

  function handleStartEdit(idx) {
    const addr = savedAddresses[idx];
    setEditIdx(idx);
    setEditForm({
      label: addr.label || "",
      street: addr.street || "",
      city: addr.city || "",
      province: addr.province || "",
      zip: addr.zip || "",
      country: addr.country || "IT",
    });
    setFormError("");
  }

  function handleCancelEdit() {
    setEditIdx(null);
    setEditForm(EMPTY_FORM);
    setFormError("");
  }

  function handleEditFormChange(field, value) {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    setFormError("");
  }

  return (
    <Drawer open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DrawerContent className="mx-auto w-full max-w-[800px] rounded-t-2xl border-[#e2e8f0] bg-white max-h-[90vh]">
        <DrawerHeader className="relative px-5 pt-5 pb-3">
          <DrawerClose asChild>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 disabled:opacity-40"
            >
              <X className="h-4 w-4" />
            </button>
          </DrawerClose>
          <DrawerTitle className="text-[18px] font-bold text-slate-900">
            {editIdx !== null ? "Modifica indirizzo" : "Indirizzo di spedizione"}
          </DrawerTitle>
          <DrawerDescription className="mt-1 text-sm text-slate-500">
            {editIdx !== null
              ? "Modifica i dettagli e conferma per usare questo indirizzo."
              : mode === "pick"
                ? "Scegli uno dei tuoi indirizzi salvati."
                : "Inserisci l'indirizzo dove vuoi ricevere l'ordine."}
          </DrawerDescription>
        </DrawerHeader>

        <div className="overflow-y-auto px-5 pb-5" style={{ maxHeight: "65vh" }}>
          {/* Edit form for a saved address */}
          {editIdx !== null && (
            <div className="space-y-4">
              <NewAddressForm value={editForm} onChange={handleEditFormChange} primaryColor={primaryColor} />
              <button
                type="button"
                onClick={handleCancelEdit}
                className="w-full text-center text-sm font-medium text-slate-500 transition hover:text-slate-700"
              >
                ← Torna agli indirizzi salvati
              </button>
            </div>
          )}

          {/* Saved addresses list */}
          {mode === "pick" && editIdx === null && (
            <div className="space-y-2.5">
              {savedAddresses.map((addr, idx) => (
                <AddressCard
                  key={addr._id || idx}
                  addr={addr}
                  selected={selectedIdx === idx}
                  primaryColor={primaryColor}
                  onSelect={() => setSelectedIdx(idx)}
                  onEdit={() => handleStartEdit(idx)}
                />
              ))}

              <button
                type="button"
                onClick={handleSwitchMode}
                className="flex w-full items-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 p-4 text-sm font-medium text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
              >
                <Plus className="h-4 w-4" />
                Usa un nuovo indirizzo
              </button>
            </div>
          )}

          {/* New address form */}
          {mode === "new" && editIdx === null && (
            <div className="space-y-4">
              <NewAddressForm value={newForm} onChange={handleFormChange} primaryColor={primaryColor} />

              {hasSaved && (
                <button
                  type="button"
                  onClick={handleSwitchMode}
                  className="w-full text-center text-sm font-medium"
                  style={{ color: primaryColor }}
                >
                  ← Usa un indirizzo salvato
                </button>
              )}
            </div>
          )}

          {/* Error messages */}
          {(formError || submitError) && (
            <div className="mt-4 flex items-start gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{formError || submitError}</span>
            </div>
          )}

          {/* Confirm button */}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-bold text-white shadow-md transition-all active:scale-[0.98] disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
          >
            {submitting ? (
              <LoaderCircle className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Check className="h-5 w-5" />
                Conferma e invia ordine
              </>
            )}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
